/* eslint-disable @typescript-eslint/no-empty-function */
import { ResourceEventHelper, ResourceScheduleHelper } from '@dispatch/shared';
import {
  useBulkUpdate,
  useCompany,
  useGetGroupAttributes,
  useGetResourcesPaginated,
  usePermissions,
} from '@dispatch/shared/hooks';
import {
  AttributeForm,
  AttributeSelection,
  BulkUpdateAttributePost,
  BulkUpdateExclusionZonesForm,
  BulkUpdatePostBody,
  EditEventForm,
  EditScheduleForm,
  Event,
  LocationForm,
  MultiSelectOption,
  ResourceBulkForm,
  Schedule,
  SelectedAttributeBoolForm,
  SelectedAttributeValuesBools,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import { BulkUpdateResource } from '@dispatch/web/features/resources';
import { useNotification } from '@dispatch/web/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import _, { isEmpty, isNull } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

const bulkUpdateSchema = yup.object().shape({
  type: yup.string().required('Type is required'),
  ids: yup.lazy((value) => {
    return Array.isArray(value)
      ? yup.array().required('Required field').typeError('Required field') // typeError is necessary here, otherwise we get a bad-looking yup error
      : yup.string().required('Required field');
  }),
});

const removeEmptyValues = (obj: any): unknown => {
  if (
    obj === null ||
    obj === undefined ||
    obj === '' ||
    (Array.isArray(obj) && obj.length === 0)
  ) {
    return undefined;
  }

  if (typeof obj === 'object' && !Array.isArray(obj)) {
    const result: any = {};
    Object.keys(obj).forEach((key) => {
      const value = removeEmptyValues(obj[key]);
      if (value !== undefined) {
        result[key] = value;
      }
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }

  return obj;
};

const allFieldsUndefined = (obj: unknown): boolean => {
  if (_.isNil(obj)) {
    return true;
  }

  if (_.isBoolean(obj)) {
    return false;
  }

  if (_.isNumber(obj)) {
    return false;
  }

  if (_.isObject(obj)) {
    return _.every(obj, (value) => allFieldsUndefined(value));
  }

  return _.isEmpty(obj);
};

/**
 * Normalizes the form's single boolean-attribute value into the array shape the
 * resourcesync DTO requires. Absence of a value (`null`/`undefined`) yields an
 * empty array; an actual boolean — including an explicit `false` — becomes a
 * one-element array. The form models `title`; the DTO wants `displayName`.
 */
const normalizeBoolToArray = (
  bool: SelectedAttributeBoolForm | null | undefined
): SelectedAttributeValuesBools[] => {
  if (!bool || _.isNil(bool.value)) {
    return [];
  }

  return [
    {
      integrationGUID: bool.integrationGUID,
      selectedAttributeDefinitionIntegrationGUID:
        bool.selectedAttributeDefinitionIntegrationGUID,
      attributeDefinitionValueIntegrationGUID:
        bool.attributeDefinitionValueIntegrationGUID,
      displayName: bool.displayName ?? bool.title ?? '',
      value: bool.value,
      startDate: bool.startDate,
      endDate: bool.endDate,
    },
  ];
};

/**
 * Assembles the bulk-update POST body from validated form values plus the
 * pre-processed events/schedules/ids the page computes (those need tenant +
 * notification context, so they are passed in).
 *
 * `attributeFilterSelection` must accompany BOTH attribute modes so
 * the backend can switch a Group-mode resource back to Single mode — Single when
 * individual attributes are staged, Group when groups are staged. It is omitted
 * entirely when neither is staged (e.g. schedule/location-only updates), so those
 * updates do not touch attribute mode.
 */
export const buildBulkUpdatePostBody = (
  values: ResourceBulkForm,
  context: {
    usedIds: string[];
    events: Event[];
    schedules: Schedule[];
  }
): BulkUpdatePostBody => {
  const { usedIds, events, schedules } = context;

  const {
    ids,
    type,
    addSelectedAttributeGUID,
    claimOwner,
    locations,
    repeatFor,
    isAppendValues,
    // Grouped-attribute bulk fields: handled explicitly below;
    // attributeKind / addSelectedGroupAttributeGUID are UI-only and dropped.
    selectedAttributeGroups,
    attributeFilterSelection,
    attributeKind,
    addSelectedGroupAttributeGUID,
    // Copy-from-resource bulk fields: copySourceResourceId is set
    // on the post explicitly below; dontCopy is UI-only and drives the per-tab
    // section stripping at the end. Both are destructured out so they do not
    // leak into `rest` and get spread onto the payload.
    copySourceResourceId,
    dontCopy,
    ...rest
  } = values;

  const isAppendValuesAsBooleans = {
    isAppendAttributes: JSON.parse(
      values?.isAppendValues?.isAppendAttributes || 'false'
    ) as boolean,
    isAppendLocations: JSON.parse(
      values?.isAppendValues?.isAppendLocations || 'false'
    ) as boolean,
    isAppendSchedules: JSON.parse(
      values?.isAppendValues?.isAppendSchedules || 'false'
    ) as boolean,
    isAppendTeamsOfficesRoles: JSON.parse(
      values?.isAppendValues?.isAppendTeamsOfficesRoles || 'false'
    ) as boolean,
    isAppendGroups: JSON.parse(
      values?.isAppendValues?.isAppendGroups || 'false'
    ) as boolean,
  };

  const post: BulkUpdatePostBody = {
    target: {
      // The caller (onBulkSave) guards that `type` is present before invoking
      // this builder, so it is always defined here.
      type: (values?.type as string).toLowerCase(),
      ids: usedIds,
    },
    isAppendValues: isAppendValuesAsBooleans,
  };

  // Copy-from-resource: forward the selected source id so the
  // backend can copy that resource's setup onto the targets.
  if (copySourceResourceId) {
    // AsyncSelect stores the selected option object ({ value, title }); the
    // backend only needs the resource id, so unwrap `.value` when present.
    const rawSource = copySourceResourceId as unknown;
    const sourceId =
      rawSource && typeof rawSource === 'object'
        ? (rawSource as { value: string | number }).value
        : (rawSource as string | number);
    if (sourceId) {
      post.copySourceResourceId = String(sourceId);
    }
  }

  if (claimOwner && !allFieldsUndefined(claimOwner)) {
    post.claimOwnerCapacity = values?.claimOwner;
  }

  const parsedLocations = locations?.map((location) => {
    const exclusionZones = {
      ...location?.exclusionZonesInformation,
      counties: (
        location.exclusionZonesInformation?.counties as MultiSelectOption[]
      )?.map((county: MultiSelectOption) => String(county.value)),
    };

    const inclusionZones = {
      ...location?.inclusionZonesInformation,
      counties: (
        location.inclusionZonesInformation?.counties as MultiSelectOption[]
      )?.map((county: MultiSelectOption) => String(county.value)),
    };

    return {
      ...location.locationInformation,
      ...location.serviceInformation,
      exclusionZones,
      inclusionZones,
    };
  });

  const postBody = removeEmptyValues({
    ...post,
    ...rest,
  }) as BulkUpdatePostBody;

  if (!isEmpty(rest?.attributes)) {
    const parseAttributes = rest?.attributes.map(
      (attribute: AttributeForm): BulkUpdateAttributePost => {
        const selectedAttributeValuesVarchars =
          attribute?.selectedAttributeValuesVarchars?.map((value) => {
            value.displayName = value.title;
            value.value = value.displayName;
            value.integrationGUID = uuidv4();
            return value;
          }) ?? [];

        return {
          ...attribute,
          selectedAttributeValuesVarchars,
          selectedAttributeValuesBools: normalizeBoolToArray(
            attribute.selectedAttributeValuesBools
          ),
        };
      }
    );

    postBody.attributes = parseAttributes;
    postBody.attributeFilterSelection =
      attributeFilterSelection ?? AttributeSelection.Single;
  }

  // Grouped-attribute path. A resource is updated in individual
  // (attributes) mode XOR grouped (selectedAttributeGroups) mode, never both.
  if (!isEmpty(selectedAttributeGroups)) {
    postBody.selectedAttributeGroups = selectedAttributeGroups?.map(
      (group) => ({
        id: group.id,
        displayName: group.displayName,
        startDate: group.startDate ?? null,
        endDate: group.endDate ?? null,
      })
    );
    postBody.attributeFilterSelection =
      attributeFilterSelection ?? AttributeSelection.Group;
  }

  if (!isEmpty(locations)) {
    postBody.locations = parsedLocations as LocationForm[];
  }

  if (!isEmpty()) {
    postBody.teamsOfficesRoles = undefined;
  }

  if (!isEmpty(events)) {
    postBody.events = events;
  }

  if (!isEmpty(schedules)) {
    postBody.schedules = schedules;
  }

  // Per-tab "Don't copy": omit that tab's section from the
  // payload so the target resources' existing values for that tab are left
  // untouched.
  if (dontCopy) {
    if (dontCopy.general) {
      postBody.generalInformation = undefined;
      // Owner capacity is part of the General tab, so it is dropped with it.
      postBody.claimOwnerCapacity = undefined;
    }
    if (dontCopy.locations) {
      postBody.locations = undefined;
    }
    if (dontCopy.teamsOfficesRoles) {
      postBody.teamsOfficesRoles = undefined;
    }
    if (dontCopy.attributes) {
      // One toggle governs the whole Attributes tab, and the tab is individual
      // attributes XOR groups, so both kinds are dropped.
      postBody.attributes = undefined;
      postBody.selectedAttributeGroups = undefined;
    }
  }

  return postBody;
};

export function BulkUpdateResourcePage() {
  const { tenantId } = useCurrentUser();
  const { company: companyData } = useCompany();
  const { permissions } = usePermissions();
  const { notifySuccess, notifyError } = useNotification();
  const [filterValue, setFilterValue] = React.useState<any | null>(null);
  const resourceData = useGetResourcesPaginated({
    filters: filterValue,
    enabled: !isNull(filterValue),
  });

  const resourceCount = React.useMemo(() => {
    return resourceData?.data?.length;
  }, [resourceData?.data]);

  // Master group-attribute list for the grouped-attribute bulk option.
  const { groupAttributesData } = useGetGroupAttributes(tenantId, !!tenantId);

  const navigate = useNavigate();

  const form = useForm<BulkUpdateExclusionZonesForm>({
    resolver: yupResolver(bulkUpdateSchema),
  });

  const onSuccess = ({ success }: { success: boolean; message?: string }) => {
    if (success) {
      notifySuccess(
        'Update Successful',
        'Bulk resources update was successful'
      );
      form.reset({}, { keepValues: false });
      navigate('/resources');
    } else {
      notifyError('Error', 'Unable to update Resources');
    }
  };

  const { upsertBulk: upsertReplace, upsertBulkStatus: upsertReplaceStatus } =
    useBulkUpdate(onSuccess);

  const companyOptions = React.useMemo(
    () => ({
      Team:
        companyData?.team?.map((team) => ({
          title: team.name,
          value: team.integrationGUID,
        })) || [],
      Office:
        companyData?.office?.map((office) => ({
          title: office.name,
          value: office.integrationGUID,
        })) || [],
      Role:
        companyData?.role?.map((role) => ({
          title: role.name,
          value: role.integrationGUID,
        })) || [],
      Group:
        companyData?.group?.map((group) => ({
          title: group.name,
          value: group.integrationGUID,
        })) || [],
    }),
    [companyData]
  );

  const createEvents = (values: any) => {
    if (!values) {
      notifyError('Error', 'Unable to Save Event');
    }

    const eventsArray = values?.map((value: EditEventForm) => {
      const events = ResourceEventHelper(value, ' ', tenantId || '');
      return events;
    });

    return eventsArray;
  };

  const createSchedules = (values: EditScheduleForm[]): Schedule[] | null => {
    if (!values) {
      notifyError('Error', 'Unable to Save Schedule');
      return null;
    }

    const processedSchedules: Schedule[] = [];

    for (const value of values) {
      const scheduleValue = {
        ...value,
        startDateTime: new Date(value.startDateTime),
        endDateTime: new Date(value.endDateTime),
      };
      const result = ResourceScheduleHelper(
        scheduleValue,
        ' ',
        processedSchedules,
        tenantId || ''
      );

      if (result.hasDuplicate) {
        const dates = result?.schedules?.map((schedule) =>
          new Date(schedule.workingDayStartTime).toDateString()
        );

        if (dates.length > 4) {
          dates.splice(3);
          dates.push('...');
        }

        notifyError(
          'Duplicate Schedule',
          `One or more days already have schedules, please revise the following dates: ${dates}`,
          { autoHideDuration: 4000 }
        );
        return null;
      }

      processedSchedules.push(...(result.schedules ?? []));
    }

    return processedSchedules;
  };

  const onBulkSave = (values: ResourceBulkForm) => {
    if (!values?.type || isEmpty(values?.ids))
      return notifyError('Error', 'Type and Ids are required');
    const usedIds: string[] = Array.isArray(values?.ids)
      ? ((() => {
          const ids = values?.ids as unknown;
          return (ids as MultiSelectOption[]).map((id) => id.value ?? id);
        })() as string[])
      : [values?.ids];

    const eventsData = values.events ? createEvents(values?.events) : [];
    const scheduleData = values?.schedules
      ? createSchedules(values?.schedules)
      : [];
    if (scheduleData === null) return;
    const schedules = scheduleData;
    const events = eventsData?.flatMap((event: Event) =>
      !isEmpty(event) ? event : []
    );

    const postBody = buildBulkUpdatePostBody(values, {
      usedIds,
      events,
      schedules,
    });

    upsertReplace(postBody);
  };

  return (
    <BulkUpdateResource
      upsertBulkStatus={upsertReplaceStatus}
      permissions={permissions}
      onBulkSave={onBulkSave}
      companyOptions={companyOptions}
      company={companyData}
      setFilterValue={setFilterValue}
      resourceCount={resourceCount}
      groupAttributesData={groupAttributesData}
    />
  );
}
